import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const QuizManager = () => {
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(null);
    const [quizResults, setQuizResults] = useState(null);

    useEffect(() => {
        fetchQuizzes();
    }, []);

    const fetchQuizzes = async () => {
        try {
            setLoading(true);
            const res = await api.get('/quizzes');
            // Handle both old format (array) and new paginated format
            const quizzesData = res.data.success && res.data.quizzes
                ? res.data.quizzes.data
                : res.data;
            setQuizzes(Array.isArray(quizzesData) ? quizzesData : []);
        } catch (error) {
            console.error('Error fetching quizzes:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchQuizResults = async (quizId) => {
        try {
            setLoading(true);
            const res = await api.get(`/quizzes/${quizId}/results`);
            // Handle both old format and new paginated format
            const resultsData = res.data.success ? {
                quiz: res.data.quiz,
                attempts: res.data.attempts?.data || res.data.attempts || [],
                stats: res.data.stats
            } : res.data;
            setQuizResults(resultsData);
            setShowResults(quizId);
        } catch (error) {
            console.error('Error fetching quiz results:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (quizId) => {
        if (window.confirm('Are you sure you want to delete this quiz? This will also delete all student attempts.')) {
            try {
                setLoading(true);
                await api.delete(`/quizzes/${quizId}`);
                fetchQuizzes();
                alert('Quiz deleted successfully!');
            } catch (error) {
                console.error('Error deleting quiz:', error);
                alert('Error deleting quiz. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'No limit';
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (showResults && quizResults) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-semibold text-slate-900">Quiz Results: {quizResults.quiz.title}</h2>
                    <button
                        onClick={() => { setShowResults(null); setQuizResults(null); }}
                        className="rounded-xl bg-slate-500 px-4 py-2 text-white hover:bg-slate-600 transition-colors"
                    >
                        Back to Quizzes
                    </button>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl">
                        <p className="text-sm text-blue-600">Total Attempts</p>
                        <p className="text-2xl font-bold text-blue-700">{quizResults.stats.totalAttempts}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl">
                        <p className="text-sm text-green-600">Average Score</p>
                        <p className="text-2xl font-bold text-green-700">{quizResults.stats.averageScore.toFixed(1)}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-xl">
                        <p className="text-sm text-purple-600">Average %</p>
                        <p className="text-2xl font-bold text-purple-700">{quizResults.stats.averagePercentage.toFixed(1)}%</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-xl">
                        <p className="text-sm text-yellow-600">Highest Score</p>
                        <p className="text-2xl font-bold text-yellow-700">{quizResults.stats.highestScore}</p>
                    </div>
                </div>

                {/* Attempts Table */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Student</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Score</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Percentage</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Time Spent</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Submitted At</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {quizResults.attempts.map((attempt, index) => (
                                    <tr key={attempt._id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">{attempt.studentId.name}</div>
                                                <div className="text-sm text-slate-500">{attempt.studentId.email}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                            {attempt.score} / {attempt.totalPoints}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                attempt.percentage >= 80 ? 'bg-green-100 text-green-800' :
                                                attempt.percentage >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {attempt.percentage.toFixed(1)}%
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                            {Math.floor(attempt.timeSpent / 60)}m {attempt.timeSpent % 60}s
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                            {formatDateTime(attempt.submittedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-slate-900">Quản lý bài tập</h2>
                <button
                    onClick={() => navigate('/admin/quiz/create')}
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 transition-colors"
                    disabled={loading}
                >
                    Tạo bài tập mới
                </button>
            </div>

            {/* Quiz List */}
            <div className="space-y-4">
                {quizzes.map((quiz) => (
                    <div key={quiz._id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-slate-900">{quiz.title}</h3>
                                {quiz.description && (
                                    <p className="text-slate-600 mt-1">{quiz.description}</p>
                                )}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {quiz.questions.length} câu hỏi
                                    </span>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        {quiz.visibleToClasses.map(c => c.name).join(', ')}
                                    </span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        quiz.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                        {quiz.isActive ? 'Đang hoạt động' : 'Không hoạt động'}
                                    </span>
                                    {quiz.timeLimit && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            {quiz.timeLimit} phút
                                        </span>
                                    )}
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        Luyện tập tự do
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                                <button
                                    onClick={() => fetchQuizResults(quiz._id)}
                                    className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                    disabled={loading}
                                >
                                    Kết quả
                                </button>
                                <button
                                    onClick={() => navigate(`/admin/quiz/edit/${quiz._id}`)}
                                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                    disabled={loading}
                                >
                                    Sửa
                                </button>
                                <button
                                    onClick={() => handleDelete(quiz._id)}
                                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                    disabled={loading}
                                >
                                    Xóa
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {quizzes.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📝</div>
                        <h3 className="text-xl font-semibold text-slate-900 mb-2">Chưa có bài tập nào</h3>
                        <p className="text-slate-600 mb-4">Tạo bài tập đầu tiên để học sinh có thể luyện tập.</p>
                        <button
                            onClick={() => navigate('/admin/quiz/create')}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
                        >
                            + Tạo bài tập đầu tiên
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizManager;